import { Box, Button, Grid } from '@mui/material'
import { sendGet, sendPost } from '../../hooks/use-fetch'
import { useEffect, useState } from 'react'
import { SelectWithLabel, type SelectOption } from '../../Components/SelectWithLabel'
import { toast } from 'react-toastify'
import { ClassroomRosterReportTableView } from './ClassroomRosterReportTableView'
import { type TimespanModel } from './PrioritizationReport.types'
import { useAccountId } from '../../ContextProviders/CurrentAccount'
import { AccountType, type lookup } from '../../core/types'

export interface ClassroomRosterReportModel {
  firstName: string
  lastName: string
  dateOfBirth: string | null
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  fplQuintile: number | null
  caregiverName: string | null
  caregiverLastName: string | null
  email: string | null
  primaryPhone: string | null
  developmentalDelay: number
  challengingBehavior: number
  primaryLanguageNotEnglish: number
  parentLowEducation: number
  abuseNeglect: number
  serviceName: string
}

export function ClassroomRosterReport (): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState<ClassroomRosterReportModel[]>([])
  const [showResults, setShowResults] = useState(false)

  const [facilityRooms, setFacilityRooms] = useState<Array<SelectOption<number>>>([])
  const [timeSpans, setTimeSpans] = useState<Array<SelectOption<number>>>([])
  const [selectedFacilityRoomID, setSelectedFacilityRoomID] = useState<number | undefined>(undefined)

  const [selectedTimeSpanID, setSelectedTimeSpanID] = useState<number | undefined>(undefined)
  const [selectedProviderID, setSelectedProviderID] = useState<number | undefined>(undefined)
  const accountId = useAccountId()
  console.log(accountId)
  const [allProviders, setAllProviders] = useState<lookup[]>()

  const GetAllProviders = async (isdId: number): Promise<void> => {
    const { response: providerResponse } = await sendGet(`/Report/ClassroomRosterReport/GetAllProviders?isdid=${isdId}`, {})
    setAllProviders(providerResponse)
  }

  useEffect(() => {
    console.log(accountId)
    if (accountId?.type === AccountType.SERVICE_PROVIDER) {
      setSelectedProviderID(accountId.id)
      setAllProviders(undefined)
    } else {
      void GetAllProviders(accountId?.id ?? -1)
    }
  }, [accountId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: timespanResponse } = await sendGet('/Report/ClassroomRosterReport/GetTimespans', {})
      if (timespanResponse != null && Array.isArray(timespanResponse)) {
        const x: TimespanModel[] = timespanResponse
        setTimeSpans(x.map(r => {
          const newOption: SelectOption<number> = {
            id: r.id, name: r.name
          }
          return newOption
        }))
      }
    }

    void fetchData()
  }, [])

  useEffect(() => {
    const fetchData = async (spId: number): Promise<void> => {
      // Fetch Facility Rooms
      const { response: roomsResponse } = await sendGet(`/Reporting/FacilityRooms?ServiceProviderID=${spId}`, {})
      if (roomsResponse != null && Array.isArray(roomsResponse)) {
        setFacilityRooms(roomsResponse)
      }
    }
    if (selectedProviderID != null) {
      void fetchData(selectedProviderID)
    }
  }, [selectedProviderID])

  const generateReport = async (): Promise<void> => {
    if (selectedFacilityRoomID === undefined) {
      toast.error('Please select a Classroom.')
      return
    }

    if (selectedTimeSpanID === undefined) {
      toast.error('Please select a Time Span.')
      return
    }

    const options = {
      facilityRoomID: selectedFacilityRoomID,
      timeSpanID: selectedTimeSpanID,
      serviceProviderID: selectedProviderID
    }

    setLoading(true)
    try {
      const { response, success } = await sendPost('/Report/ClassroomRosterReport/GetClassroomRosterReport', options)
      if (success) {
        setReportData(response)
        setShowResults(true)
      } else {
        toast.error('Failed to generate report.')
      }
    } catch (error) {
      toast.error('An error occurred while generating the report.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Grid container columns={7} gap={1} sx={{ display: 'flex', flexDirection: 'row' }}>
        {accountId != null && accountId.type !== AccountType.SERVICE_PROVIDER && (
          <Grid item xs={3}>
            <SelectWithLabel
              includeNoneOption={false}
              name="serviceProviderID"
              label="Service Provider"
              value={selectedProviderID}
              width="100%"
              options={allProviders ?? []}
              onChange={(e) => { setSelectedProviderID(Number(e.target.value)) }}
            />
          </Grid>
        )}
        <Grid item xs={3}>
          <SelectWithLabel
            includeNoneOption={false}
            name="facilityRoomID"
            label="Classroom"
            value={selectedFacilityRoomID}
            width="100%"
            options={facilityRooms}
            onChange={(e) => { setSelectedFacilityRoomID(Number(e.target.value)) }}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectWithLabel
            includeNoneOption={false}
            name="timeSpanID"
            label="Time Span"
            value={selectedTimeSpanID}
            width="100%"
            options={timeSpans}
            onChange={(e) => { setSelectedTimeSpanID(Number(e.target.value)) }}
          />
        </Grid>
      </Grid>

      <Box>
        <Button
          color='secondary'
          variant='contained'
          onClick={generateReport}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </Button>
      </Box>

      {showResults &&
        <Box>
          <ClassroomRosterReportTableView reportData={reportData} loading={loading} />
        </Box>
      }
    </>
  )
}
